/**
 * 첫 화면 인트로 오버레이 (`/` 경로).
 *
 * 링크를 타고 온 사람은 가입 없이 곧장 월드로 들어간다. 그 사이 몇 초 동안
 * 게이트웨이 연결 → 체험 계정 → 캐릭터 생성 → 월드 진입이 차례로 일어나는데,
 * 이 화면은 그 시간을 "세계가 조립되는 시간"으로 보여준다.
 *
 * ## 왜 타일인가
 *
 * 이 클라이언트의 정체는 2:1 아이소메트릭 마름모 격자다(`utils/isometric.ts`,
 * TILE 48×24). 인트로가 걷히면 바로 그 격자가 깔린 게임 화면이 나오므로, 로딩
 * 스피너 대신 같은 격자를 쓴다. 타일이 켜지는 파도는 **맨해튼 거리**를 따르는데,
 * 이것도 장식이 아니라 서버가 시야·채팅 범위를 재는 바로 그 거리다
 * (`Point.isInScreen()`, 19타일). 진행 단계가 오를수록 켜지는 반경이 넓어져
 * 격자 자체가 진행 표시를 겸한다 — 별도의 막대가 필요 없다.
 *
 * 색은 게임 화면에서 그대로 가져왔다(배경 #1a1a2e, 벽 #c9705a, UI 금색). 인트로가
 * 끝나는 순간 같은 팔레트의 월드가 나타나므로 전환이 끊기지 않는다.
 *
 * Phaser 씬(Login → CharSelect → Game)이 그 아래에서 바뀌는 동안 이 오버레이는
 * DOM에 그대로 떠 있는다. 씬 전환마다 화면이 깜빡이지 않게 하려는 것이다.
 */

import { networkEvents } from '../core/EventEmitter';

/** 진행 단계. 순서대로 올라가며, 뒤로 돌아가지 않는다. */
export enum IntroStage {
  CONNECTING = 0,
  ACCOUNT = 1,
  CHARACTER = 2,
  ENTERING = 3,
}

const STAGE_LABELS: Record<IntroStage, string> = {
  [IntroStage.CONNECTING]: '서버에 연결하는 중',
  [IntroStage.ACCOUNT]: '체험 계정을 준비하는 중',
  [IntroStage.CHARACTER]: '캐릭터를 만드는 중',
  [IntroStage.ENTERING]: '월드로 들어가는 중',
};

/** 단계별로 켜지는 타일 반경(맨해튼 거리). 격자 절반이 GRID_RADIUS다. */
const STAGE_RADIUS: Record<IntroStage, number> = {
  [IntroStage.CONNECTING]: 3,
  [IntroStage.ACCOUNT]: 6,
  [IntroStage.CHARACTER]: 9,
  [IntroStage.ENTERING]: 12, // 격자 끝(GRID_RADIUS*2)까지 — 섬이 다 켜진다
};

/** 격자 반경. 13×13 = 169칸 — DOM 부담 없이 파도가 보일 만한 크기다. */
const GRID_RADIUS = 6;

const STYLE_ID = 'l1j-intro-style';
const ROOT_ID = 'l1j-intro';

const CSS = `
#${ROOT_ID} {
  --void: #07080f;
  --deep: #12162e;
  /* 아직 켜지지 않은 타일. 배경보다 확실히 밝아야 섬의 윤곽이 먼저 읽힌다. */
  --tile-off: #232a4e;
  --tile-on: #3d4a8c;
  /* 파도의 앞머리 한 겹 — 켜진 곳보다 한 단계 밝을 뿐, 색을 바꾸지는 않는다.
     여기에 주황을 쓰면 다이아몬드 둘레 전체가 주황이 되어 강조가 주조색이 된다. */
  --tile-edge: #5a6ac0;
  --ember: #c9705a;
  --gold: #d8ad4e;
  --mist: #7f89b8;

  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(24px, 5vh, 56px);
  padding: 24px;
  background:
    radial-gradient(120% 90% at 50% 42%, #171d3d 0%, var(--deep) 38%, var(--void) 78%);
  color: #e8ecff;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  opacity: 1;
  transition: opacity 620ms ease, transform 620ms ease;
  overflow: hidden;
}

/* 월드로 넘어갈 때: 격자가 시야로 밀려들어오며 걷힌다 */
#${ROOT_ID}.is-leaving {
  opacity: 0;
  transform: scale(1.12);
  pointer-events: none;
}

/* ---------------------------------------------------------- 타일 격자 */

#${ROOT_ID} .field {
  position: relative;
  width: min(74vmin, 540px);
  height: min(74vmin, 540px);
  flex: none;
  /* 세로를 반으로 눌러 그리므로 실제 차지하는 높이는 절반이다.
     남는 위아래 여백을 걷어내 제목과의 간격을 제 것으로 만든다. */
  margin: min(-18vmin, -135px) 0;
}

#${ROOT_ID} .grid {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: repeat(${GRID_RADIUS * 2 + 1}, 1fr);
  gap: 2px;
  /* 정사각 격자를 45도 돌린 뒤 세로를 반으로 눌러 2:1 마름모를 만든다 —
     게임의 screenX=(tx-ty)*24 / screenY=(tx+ty)*12 와 같은 비율이다.
     transform은 오른쪽부터 적용되므로 압축(scaleY)을 왼쪽에 둬야 회전 결과에
     걸린다. 순서를 뒤집으면 눌린 사각형을 돌린 모양이 되어 축이 어긋난다. */
  transform: scaleY(0.5) rotate(45deg);
}

/* 꺼진 타일도 보여야 파도가 어디까지 왔는지 읽힌다 —
   섬 전체의 윤곽이 먼저 있고, 그 위로 불이 번져 가는 그림이다. */
#${ROOT_ID} .grid i {
  display: block;
  aspect-ratio: 1;
  background: var(--tile-off);
  opacity: 0.5;
  transform: scale(0.86);
  transition: opacity 520ms ease, background-color 520ms ease, transform 520ms ease;
  transition-delay: calc(var(--d) * 26ms);
}

#${ROOT_ID} .grid i.on {
  background: var(--tile-on);
  opacity: 1;
  transform: scale(1);
}

/* 파도의 앞머리 한 겹 — 어디까지 왔는지 눈으로 읽힌다 */
#${ROOT_ID} .grid i.edge {
  background: var(--tile-edge);
  opacity: 1;
}

/* 화면에서 색이 튀는 곳은 여기 하나뿐이다. 곧 내 캐릭터가 설 자리다. */
#${ROOT_ID} .grid i.core {
  background: var(--gold);
  opacity: 1;
  transform: scale(1);
}

#${ROOT_ID} .pulse {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 38%;
  height: 38%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(216,173,78,0.30) 0%, rgba(216,173,78,0) 68%);
  animation: l1j-breathe 3.4s ease-in-out infinite;
  pointer-events: none;
}

@keyframes l1j-breathe {
  0%, 100% { opacity: 0.45; transform: scale(0.94); }
  50%      { opacity: 0.95; transform: scale(1.06); }
}

/* ---------------------------------------------------------- 글자 */

#${ROOT_ID} .plate {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
}

#${ROOT_ID} .title {
  margin: 0;
  font-size: clamp(38px, 9vmin, 76px);
  font-weight: 300;
  /* 두 글자짜리 제목이라 자간을 더 벌리면 한 낱말로 읽히지 않는다 */
  letter-spacing: 0.13em;
  /* 자간은 마지막 글자 뒤에도 붙어 가운데가 그만큼 왼쪽으로 밀린다 */
  text-indent: 0.13em;
  color: #f2f4ff;
  line-height: 1;
}

#${ROOT_ID} .sub {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: clamp(10px, 1.6vmin, 12px);
  letter-spacing: 0.22em;
  color: var(--mist);
}

#${ROOT_ID} .status {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: clamp(12px, 2vmin, 14px);
  letter-spacing: 0.06em;
  color: var(--gold);
  min-height: 1.4em;
}

#${ROOT_ID} .status .mark {
  color: var(--ember);
  animation: l1j-blink 1.15s steps(2, start) infinite;
}

@keyframes l1j-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.15; }
}

#${ROOT_ID} .status.is-error { color: #ff9a8a; }
#${ROOT_ID} .status.is-error .mark { animation: none; opacity: 1; }

#${ROOT_ID} .foot {
  position: absolute;
  bottom: clamp(18px, 4vh, 34px);
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 12.5px;
  color: var(--mist);
}

#${ROOT_ID} .foot .dot { opacity: 0.45; }

#${ROOT_ID} .foot a,
#${ROOT_ID} .retry {
  color: #cdd5ff;
  text-decoration: none;
  border-bottom: 1px solid rgba(205,213,255,0.35);
  padding-bottom: 1px;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font: inherit;
  cursor: pointer;
  transition: color 160ms ease, border-color 160ms ease;
}

#${ROOT_ID} .foot a:hover,
#${ROOT_ID} .retry:hover {
  color: var(--gold);
  border-color: var(--gold);
}

#${ROOT_ID} a:focus-visible,
#${ROOT_ID} .retry:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 3px;
  border-radius: 2px;
}

@media (prefers-reduced-motion: reduce) {
  #${ROOT_ID} .grid i { transition-delay: 0ms !important; transition-duration: 0ms !important; }
  #${ROOT_ID} .pulse,
  #${ROOT_ID} .status .mark { animation: none; }
  #${ROOT_ID}.is-leaving { transform: none; }
}
`;

export class IntroOverlay {
  private root: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private tiles: HTMLElement[] = [];
  /** 타일별 중심으로부터의 맨해튼 거리 */
  private distances: number[] = [];
  private stage: IntroStage = IntroStage.CONNECTING;
  private dismissed = false;
  private unsubscribers: Array<() => void> = [];

  mount(): void {
    if (this.root) return;

    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    const root = document.createElement('div');
    root.id = ROOT_ID;
    root.setAttribute('role', 'status');
    root.setAttribute('aria-live', 'polite');
    root.innerHTML = `
      <div class="field">
        <div class="grid"></div>
        <div class="pulse"></div>
      </div>
      <div class="plate">
        <h1 class="title">아덴</h1>
        <p class="sub">L1J-JP 3.63C · WEB CLIENT</p>
        <p class="status"><span class="mark">▸</span><span class="text">${STAGE_LABELS[IntroStage.CONNECTING]}</span></p>
      </div>
      <div class="foot">
        <span>가입 없이 바로 시작합니다</span>
        <span class="dot">·</span>
        <a href="/login">계정으로 로그인</a>
      </div>
    `;

    document.body.appendChild(root);
    this.root = root;
    this.statusEl = root.querySelector('.status');

    this.buildGrid(root.querySelector('.grid') as HTMLElement);
    this.applyStage(IntroStage.CONNECTING);
    this.listen();
  }

  /** 격자를 만들고 각 칸에 중심으로부터의 맨해튼 거리를 심는다. */
  private buildGrid(grid: HTMLElement): void {
    const size = GRID_RADIUS * 2 + 1;
    const frag = document.createDocumentFragment();

    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        const d = Math.abs(row - GRID_RADIUS) + Math.abs(col - GRID_RADIUS);
        const cell = document.createElement('i');
        cell.style.setProperty('--d', String(d));
        if (d === 0) cell.classList.add('core');
        frag.appendChild(cell);
        this.tiles.push(cell);
        this.distances.push(d);
      }
    }
    grid.appendChild(frag);
  }

  /** 서버 신호를 그대로 단계로 옮긴다. 씬을 거치지 않아 전환과 무관하게 이어진다. */
  private listen(): void {
    const on = (event: string, handler: (payload: unknown) => void) => {
      networkEvents.on(event, handler);
      this.unsubscribers.push(() => networkEvents.off(event, handler));
    };

    on('login_result', (payload) => {
      const result = (payload as { result?: number } | undefined)?.result;
      if (result === 0) this.setStage(IntroStage.ACCOUNT);
    });

    // 체험 계정은 캐릭터를 그 자리에서 만든다 (CharSelectScene)
    on('char_create_result', () => this.setStage(IntroStage.CHARACTER));

    // 캐릭터가 이미 있는 재방문자는 생성 없이 곧장 목록을 받는다
    on('char_list', () => {
      if (this.stage < IntroStage.CHARACTER) this.setStage(IntroStage.CHARACTER);
    });

    // 월드 진입 신호 — 게이트웨이는 char_spawn 대신 own_char_status로 알린다
    on('own_char_status', () => this.setStage(IntroStage.ENTERING));
  }

  setStage(stage: IntroStage): void {
    if (this.dismissed || stage <= this.stage) return;
    this.applyStage(stage);
  }

  private applyStage(stage: IntroStage): void {
    this.stage = stage;
    const radius = STAGE_RADIUS[stage];

    this.tiles.forEach((tile, i) => {
      const d = this.distances[i];
      tile.classList.toggle('on', d <= radius);
      tile.classList.toggle('edge', d === radius);
    });

    this.setStatus(STAGE_LABELS[stage]);
  }

  private setStatus(text: string): void {
    const el = this.statusEl?.querySelector('.text');
    if (el) el.textContent = text;
  }

  /**
   * 연결에 실패했을 때. 스스로 회복할 방법이 없으므로 무엇을 할 수 있는지 알린다.
   */
  showError(message: string): void {
    if (this.dismissed || !this.root) return;
    this.statusEl?.classList.add('is-error');
    this.setStatus(message);

    const foot = this.root.querySelector('.foot');
    if (foot && !foot.querySelector('.retry')) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'retry';
      retry.textContent = '다시 시도';
      retry.addEventListener('click', () => window.location.reload());
      foot.prepend(retry, Object.assign(document.createElement('span'), {
        className: 'dot',
        textContent: '·',
      }));
    }
  }

  /** 월드가 준비됐다. 격자를 시야 쪽으로 밀어내며 걷는다. */
  dismiss(): void {
    if (this.dismissed || !this.root) return;
    this.dismissed = true;

    const root = this.root;
    root.classList.add('is-leaving');
    this.unsubscribers.forEach((off) => off());
    this.unsubscribers = [];

    window.setTimeout(() => {
      root.remove();
      this.root = null;
    }, 700);
  }

  get isDismissed(): boolean {
    return this.dismissed;
  }
}

/** 앱 전체에서 하나만 쓴다 (`/` 경로에서만 mount된다). */
export const introOverlay = new IntroOverlay();
